import ace from 'ace-builds';
import {convertChevrotainErrors} from 'apstra-ui-common';
import {compact, escape, includes, last, map, reverse} from 'lodash';

import CustomTelemetryExpressionParser from './CustomTelemetryExpressionParser';
import CustomTelemetryExpressionTypeResolver from './CustomTelemetryExpressionTypeResolver';
import buildPropertyDocHTMLFromSchema from '../pythonExpression/buildPropertyDocHTMLFromSchema';

export const CUSTOM_TELEMETRY_EXPRESSION_KEYWORDS = ['in', 'if', 'else', 'not', 'and', 'or'];

export const BUILTIN_FUNCTIONS = [
  {
    name: 'int',
    args: 'arg[, base=0]',
    description: 'Converts arg to integer.',
    returnType: {type: 'number'},
  },
  {
    name: 'float',
    args: 'arg',
    description: 'Converts arg to float.',
    returnType: {type: 'number'},
  },
  {
    name: 'str',
    args: 'arg',
    description: 'Converts arg to string.',
    returnType: {type: 'string'},
  },
  {
    name: 'len',
    args: 'arg',
    description: 'Returns length of the argument arg.',
    returnType: {type: 'number'},
  },
  {
    name: 'flag',
    args: 'arg',
    description: 'Returns 0 if arg is None or 1 if not.',
    returnType: {type: 'number'},
  },
  {
    name: 're_match',
    args: 'pattern, str',
    description: 'Returns string result of pattern regular expression matching the beginning of str, ' +
        'or empty string if no match.',
    returnType: {type: 'string'},
  },
  {
    name: 're_search',
    args: 'pattern, str',
    description: 'Search through str and return the string result at the first location of pattern match, ' +
        'or empty string if no match could be found.',
    returnType: {type: 'string'},
  },
  {
    name: 'sorted',
    args: 'arg[, reverse]',
    description: 'Returns a new sorted list from the items in arg.',
    returnType: {type: 'array'},
  }
];

export const BUILTIN_STRING_METHODS = [
  {
    name: 'startswith',
    args: 'prefix',
    description: 'Returns True if string starts with the specified prefix. prefix argument can be a list' +
        ' of prefixes to look for.'
  },
  {
    name: 'endswith',
    args: 'suffix',
    description: 'Returns True if string ends with the specified suffix. suffix argument can be a list of' +
        ' suffixes to look for.'
  },
  {
    name: 'find',
    args: 'arg[, start[, end]]',
    description: 'Returns the lowest index in the str where substring arg can be found, within the optional' +
        ' range [start : end]. Returns -1 if arg could not be found.',
    returnType: {type: 'number'},
  },
  {
    name: 'rfind',
    args: 'arg[, start[, end]]',
    description: 'Returns the highest index in the str where substring arg can be found, within the optional' +
        ' range [start, end]. Returns -1 if arg could not be found.',
    returnType: {type: 'number'},
  },
  {
    name: 'count',
    args: 'arg[, start[, end]]',
    description: 'Returns number of occurrences of arg in the optional range [start, end]',
    returnType: {type: 'number'},
  },
  {
    name: 'lower',
    description: 'Returns a copy of the string in lower case.',
    returnType: {type: 'string'},
  },
  {
    name: 'upper',
    description: 'Returns a copy of the string in upper case.',
    returnType: {type: 'string'},
  },
  {
    name: 'replace',
    args: 'old, new[, count]',
    description: 'Returns a copy of the string with all or count occurrences of substring old replaced by new.',
    returnType: {type: 'string'},
  },
  {
    name: 'split',
    args: '[sep][, maxsplit]',
    description: 'If sep is given, then consecutive delimiters will not be groupd together. If maxsplit is given,' +
        ' than at most maxsplit splits are done.'
  },
  {
    name: 'strip',
    args: '[chars]',
    description: 'Returns a copy of the str with the leading and trailing chars or whitespace removed.',
    returnType: {type: 'string'},
  },
  {
    name: 'join',
    args: 'iterable',
    description: 'Returns a string which is the concatenation of the strings in the iterable ' +
      '(list, dictionary keys, or string) using the string as a separator.',
    returnType: {type: 'string'},
  },
  {
    name: 'get',
    args: 'dictionary[, default]',
    description: 'Returns the value of the specified key from the dictionary. If the key is not found, returns None.',
    returnType: {type: 'any'},
  }
];

const BUILTIN_NUMBER_METHODS = [
];

const makeStringState = (stateName, encloser, token = 'string') => ({
  [stateName]: [{
    token,
    regex: encloser + '|$',
    next: 'start'
  }, {
    defaultToken: token
  }]
});

function buildFunctionDocHTML(name, args, description) {
  return compact([
    '<b>', escape(name), '(</b>', args ? '<i>' + args + '</i>' : null, '<b>)</b>',
    description ? '<p>' + escape(description) + '</p>' : null
  ]).join('');
}

const leftToRightParens = {
  '(': ')',
  '[': ']',
  '{': '}',
};

const identifier = '[a-zA-Z_]\\w*\\b';

ace.define(
  'ace/mode/custom-telemetry-expression',
  ['require', 'exports', 'module'],
  (require, exports) => {
    const {TextHighlightRules} = require('ace/mode/text_highlight_rules');
    const {Mode: TextMode} = require('ace/mode/text');
    const {BaseCompleter} = require('ace/base_completer');
    const {TokenIterator} = require('ace/token_iterator');

    const KEYWORDS_COMPLETIONS = map(CUSTOM_TELEMETRY_EXPRESSION_KEYWORDS, (name, index) => ({
      caption: name,
      snippet: name,
      meta: 'keyword',
      score: 100 - index,
    }));

    const BUILTIN_FUNCTIONS_COMPLETIONS = map(
      BUILTIN_FUNCTIONS,
      ({name, args, description, meta}, index) => ({
        caption: name,
        snippet: name + '($0)',
        meta: meta ?? 'function',
        className: 'completion-function ace_',
        docHTML: buildFunctionDocHTML(name, args, description),
        score: 100 - index,
      })
    );

    const BUILTIN_STRING_METHODS_COMPLETIONS = map(
      BUILTIN_STRING_METHODS,
      ({name, args, description, meta}, index) => ({
        caption: name,
        snippet: name + '($0)',
        meta: meta ?? 'method',
        className: 'completion-function ace_',
        docHTML: buildFunctionDocHTML(name, args, description),
        score: 100 - index,
      })
    );

    const BUILTIN_NUMBER_METHODS_COMPLETIONS = map(
      BUILTIN_NUMBER_METHODS,
      ({name, args, description, meta}, index) => ({
        caption: name,
        snippet: name + '($0)',
        meta: meta ?? 'method',
        className: 'completion-function ace_',
        docHTML: buildFunctionDocHTML(name, args, description),
        score: 100 - index,
      })
    );

    class CustomTelemetryExpressionHighlightRules extends TextHighlightRules {
      constructor() {
        super();

        const keywordMapper = this.createKeywordMapper({
          'constant.language': 'None|True|False',
          'keyword.operator': CUSTOM_TELEMETRY_EXPRESSION_KEYWORDS.join('|'),
        }, 'identifier');

        this.$rules = {
          start: [
            {
              token: 'string',
              regex: '"(?=.)',
              next: 'short-double-quote-string'
            },
            {
              token: 'string',
              regex: "'(?=.)",
              next: 'short-single-quote-string'
            },
            {
              token: 'keyword.operator',
              regex: /(?:==|!=|>=|<=|<|>|\/\/?|%|\*\*?|\+|-)/,
            },
            {
              token: ['entity.function.name', 'paren.lparen'],
              regex: '(' + identifier + ')(\\()'
            },
            {
              token: keywordMapper,
              regex: identifier
            },
            {
              token: 'punctuation',
              regex: ',|:|\\.'
            },
            {
              token: 'paren.lparen',
              regex: /[[({]/
            },
            {
              token: 'paren.rparen',
              regex: /[\])}]/
            },
            {
              token: 'text',
              regex: /\s+/
            },
            {
              include: 'constants'
            }
          ],
          ...makeStringState('short-double-quote-string', '"'),
          ...makeStringState('short-single-quote-string', "'"),
          constants: [
            {
              token: 'constant.numeric',
              regex: /-?(0|[1-9]\d*)(\.\d+)?([eE][+-]?\d+)?/
            },
            {
              token: ['punctuation', 'function.support', 'paren.lparen'],
              regex: '(\\.)(' + identifier + ')(\\()'
            },
          ]
        };
        this.normalizeRules();
      }
    }

    class CustomTelemetryExpressionCompleter extends BaseCompleter {
      getKnownVariablesCompletions(knownVariables) {
        return map(knownVariables, ({propertyName, propertySchema, meta, color, shape}, name, index) => ({
          caption: name,
          snippet: name,
          docHTML: '<div><b>' + escape(propertyName) + '</b></div>' + buildPropertyDocHTMLFromSchema(propertySchema),
          className: map(
            compact([
              'variable',
              shape ? 'completion_icon_' + shape : null,
              color ? 'completion_icon_color_' + color : null,
            ]),
            (className) => className + ' ace_'
          ).join(''),
          meta,
          score: 2000 - index,
        }));
      }

      checkForCustomCharacterCompletions(editor) {
        const pos = editor.getCursorPosition();
        const line = editor.getSession().getLine(pos.row).substr(0, pos.column);
        if (/(['"\w\])}](\.|\[)\s*)$/.test(line)) {
          this.showCompletionsPopup(editor);
        }
      }

      getChainedCompletions(state, session, pos, prefix, completeFor) {
        const token = session.getTokenAt(pos.row, pos.column);
        const iterator = new TokenIterator(session, pos.row, pos.column);
        const isDotSyntax = completeFor === '.';

        let currentToken = token;
        const tokens = [];
        const parenStack = [];
        try {
          do {
            tokens.unshift(currentToken);
            currentToken = iterator.stepBackward();
            if (!currentToken) break;
            if (currentToken.type === 'paren.rparen') {
              parenStack.push(...reverse(currentToken.value.split('')));
            }
            if (currentToken.type === 'paren.lparen') {
              if (!parenStack.length) {
                break;
              }
              if (last(parenStack) === leftToRightParens[currentToken.value]) {
                parenStack.pop();
              } else {
                throw new Error('invalid parens');
              }
            }
            if (!parenStack.length && includes(['text', 'keyword.operator'], currentToken.type)) {
              break;
            }

          // eslint-disable-next-line no-constant-condition
          } while (true);
        } catch {
          tokens.length = 0;
        }
        const tokenString = map(tokens, 'value').join('');
        if (tokenString) {
          const lastIndex = tokenString.lastIndexOf(isDotSyntax ? '.' : '[');
          const tokenStringBeforeLastDot = tokenString.substring(0, lastIndex);
          const {cst} = CustomTelemetryExpressionParser.parse(tokenStringBeforeLastDot);
          if (cst) {
            const resolvedType = CustomTelemetryExpressionTypeResolver.resolveType(cst, session.completerParams);
            if (isDotSyntax) {
              if (resolvedType?.type === 'string') {
                return BUILTIN_STRING_METHODS_COMPLETIONS;
              }
              if (resolvedType?.type === 'number') {
                return BUILTIN_NUMBER_METHODS_COMPLETIONS;
              }
            }
            if (!isDotSyntax || resolvedType?.type === 'object') {
              let i = 0;
              const result = map(resolvedType?.properties, (propertySchema, propertyName) => ({
                caption: isDotSyntax ? propertyName : `"${propertyName}"`,
                snippet: isDotSyntax ? propertyName : `"${propertyName}"`,
                className: isDotSyntax ? undefined : 'string',
                meta: 'property',
                score: 5000 + (++i), // Lower score than keywords/builtins
              }));
              return result;
            }
            return [];
          }
        }
        return [];
      }

      resolveTypes(cst, knownVariableTypes) {
        if (!cst) return [];

        if (cst.children.Identifier && cst.children.indexingOrSlicingChain) {
          const identifier = cst.children.Identifier[0].image;
          const identifierType = knownVariableTypes?.[identifier];

          if (!identifierType) return [];

          return this.resolveTypes(
            cst.children.indexingOrSlicingChain[0],
            identifierType.properties
          );
        }

        if (cst.children.Identifier) {
          const identifier = cst.children.Identifier[0].image;
          const identifierType = knownVariableTypes?.[identifier];
          return [identifierType];
        }

        return [];
      }

      async getCustomCompletions(state, session, pos, prefix) {
        const token = session.getTokenAt(pos.row, pos.column);
        const result = [];

        if (token.type === 'string' || token.type === 'constant.language.escape') return [];

        if (token.type === 'punctuation' && token.value === '.') {
          return this.getChainedCompletions(state, session, pos, prefix, '.');
        }

        if (token.type === 'paren.lparen' && token.value === '[') {
          return this.getChainedCompletions(state, session, pos, prefix, '[');
        }

        if (token.type === 'identifier') {
          result.push(
            ...KEYWORDS_COMPLETIONS,
            ...BUILTIN_FUNCTIONS_COMPLETIONS,
            ...this.getKnownVariablesCompletions(session.completerParams?.knownVariables),
            ...this.getChainedCompletions(state, session, pos, prefix, '.'),
            ...await this.getTextCompletions(state, session, pos, prefix)
          );
        }
        return result;
      }
    }

    class CustomTelemetryExpressionMode extends TextMode {
      $id = 'ace/mode/custom-telemetry-expression';
      $behaviour = this.$defaultBehaviour;
      HighlightRules = CustomTelemetryExpressionHighlightRules;
      completer = new CustomTelemetryExpressionCompleter();

      lineCommentStart = '#';

      getNextLineIndent(state, line, tab) {
        let indent = this.$getIndent(line);
        if (state === 'start') {
          if (line.match(/^.*[{([:]\s*$/)) indent += tab;
        }
        return indent;
      }

      validate(text) {
        try {
          const {lexErrors, parseErrors} = CustomTelemetryExpressionParser.parse(text);
          return convertChevrotainErrors(lexErrors, parseErrors);
        } catch {}
        return [];
      }
    }

    exports.Mode = CustomTelemetryExpressionMode;
    exports.HighlightRules = CustomTelemetryExpressionHighlightRules;
    exports.Completer = CustomTelemetryExpressionCompleter;
  }
);

ace.require(['ace/mode/custom-telemetry-expression']);
